import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import type { AppliedShippingFee } from './useShippingFees';
import type { ShippingFee } from '@/services/payments.service';

export interface PaymentShippingSectionProps {
  appliedFees: AppliedShippingFee[];
  shippingTotal: number;
  availableFees: ShippingFee[];
  onRemove: (id: number) => void;
  onAdd: (id: number) => void;
  /**
   * Product total to add the shipping to for the closing Total row. Omit to
   * render the fees with no total, for callers that print their own.
   */
  productsTotal?: number;
  /** Display-only (payment detail, review step): no add or remove controls. */
  readOnly?: boolean;
}

/**
 * Shipping on a payment, rendered as a strip attached beneath the product lines
 * and their discount/total rows — never as a line among them, because shipping is
 * not a product and must not read as one. It sits with the products so the figure
 * staff read at the top reconciles with what is actually charged; the charge
 * breakdown lower down carries a summary line only.
 *
 * Deliberately shaped as the same three-column table as the product rows above
 * (right-aligned label, font-mono amount, trailing action) so the strip reads as
 * a continuation of them rather than a separate widget.
 *
 * One row per distinct fee. Staff may remove a fee or add one back, but the
 * amount is deliberately not editable: it comes from the account's shipping-fee
 * setup, and the server re-resolves it on save.
 */
export function PaymentShippingSection({
  appliedFees,
  shippingTotal,
  availableFees,
  onRemove,
  onAdd,
  productsTotal,
  readOnly = false,
}: PaymentShippingSectionProps) {
  const [adding, setAdding] = useState(false);

  const canAdd = !readOnly && availableFees.length > 0;
  // The account has shipping set up at all. Distinct from canAdd: once every fee
  // is applied there is nothing left to add, but the section still belongs here.
  const accountHasFees = appliedFees.length > 0 || availableFees.length > 0;
  // Only worth a Total once shipping actually moves it — with no fees applied the
  // products' own total above is already the total, and repeating it reads as a
  // second, conflicting figure.
  const showTotal = typeof productsTotal === 'number' && shippingTotal > 0;

  // An account with no shipping fees configured gets no strip at all.
  if (!accountHasFees && !showTotal) return null;

  const options: ComboboxOption<number>[] = availableFees.map((fee) => ({
    value: fee.shippingFeeID,
    label: `${fee.name} — $${fee.amount.toFixed(2)}`,
  }));

  // Matches the action column the product rows reserve, so the amounts stay in
  // the same vertical line whether or not the row has a control.
  const actionCell = !readOnly;

  // One fee reads just "Shipping" (SCRUM-2023: the patient sees a shipping
  // fee, not which catalog row it came from). Only when several distinct fees
  // are applied does each keep its name — otherwise two identical "Shipping"
  // rows would be indistinguishable, and so would their remove buttons.
  const feeLabel = (fee: AppliedShippingFee) =>
    appliedFees.length > 1 && fee.name ? `Shipping — ${fee.name}` : 'Shipping';

  return (
    <table className="w-full text-sm">
      <tbody>
        {appliedFees.map((fee) => (
          <tr key={fee.id} className="bg-slate-50 text-xs">
            <td className="px-2 py-1 text-right">{feeLabel(fee)}:</td>
            <td className="w-[9%] px-1 py-1 text-right font-mono text-slate-900">
              ${fee.amount.toFixed(2)}
            </td>
            {actionCell && (
              <td className="w-[4%] px-1 py-1 text-center">
                <button
                  type="button"
                  className="text-red-500 hover:text-red-700"
                  onClick={() => onRemove(fee.id)}
                  title={`Remove ${fee.name} shipping fee`}
                  aria-label={`Remove ${fee.name} shipping fee`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            )}
          </tr>
        ))}

        {/* Rendered whenever the section is editable, not only when something is
            addable: a control that silently disappears once the last fee is
            applied reads as a broken form. When nothing is left it says so. */}
        {!readOnly && accountHasFees && (
          <tr className="bg-slate-50 text-xs">
            <td colSpan={actionCell ? 3 : 2} className="px-2 py-1 text-right">
              {!canAdd ? (
                <span className="text-slate-500">All shipping fees applied</span>
              ) : adding ? (
                <div className="ml-auto w-56">
                  <SearchableCombobox
                    options={options}
                    value={null}
                    onChange={(value) => {
                      if (typeof value === 'number') onAdd(value);
                      setAdding(false);
                    }}
                    placeholder="Select a shipping fee…"
                  />
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  leftIcon={<Plus className="h-3 w-3" aria-hidden="true" />}
                  onClick={() => setAdding(true)}
                >
                  Add shipping
                </Button>
              )}
            </td>
          </tr>
        )}

        {showTotal && (
          <tr className="border-t border-slate-200 bg-slate-100">
            <td className="px-2 py-1 text-right font-bold">Total:</td>
            <td className="w-[9%] px-1 py-1 text-right font-mono font-bold">
              ${((productsTotal as number) + shippingTotal).toFixed(2)}
            </td>
            {actionCell && <td className="w-[4%]" />}
          </tr>
        )}
      </tbody>
    </table>
  );
}
